import type React from 'react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  SignInPage, SignInCard, SignInWordmark, SignInTagline,
  Input, PrimaryButton, SecondaryButton, Divider, InlineError, AlertBanner,
} from '../styled-components';
import { WaveformBackground } from '../components';
import { getCSRFToken, CSRF_HEADER_NAME } from '../../common/utils';
import { useFetchData } from '../hooks';

type SignInState =
  | { step: 'idle' }
  | { step: 'email_input' }
  | { step: 'sending' }
  | { step: 'send_error'; message: string }
  | { step: 'otp_input'; otpError?: string }
  | { step: 'verifying' };

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="currentColor" fillOpacity="0.9" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="currentColor" fillOpacity="0.75" />
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="currentColor" fillOpacity="0.6" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="currentColor" fillOpacity="0.85" />
    </svg>
  );
}

const SEND_ERROR_MESSAGE = 'Failed to send code. Please try again.';

async function checkSSO(email: string, signal?: AbortSignal): Promise<boolean> {
  const res = await fetch('/dashboard/api/check-sso', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      [CSRF_HEADER_NAME]: getCSRFToken(),
    },
    body: JSON.stringify({ email }),
    signal,
  });
  if (!res.ok) throw new Error(`check-sso failed: HTTP ${res.status}`);
  const data = await res.json();
  return data.sso_required;
}

// Whether the email's domain is SSO-locked, checked after typing pauses.
function useSSORequired(email: string): boolean {
  const [result, setResult] = useState<{ email: string; ssoRequired: boolean } | null>(null);

  useEffect(() => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setResult({ email, ssoRequired: await checkSSO(email, controller.signal) });
      } catch (e) {
        if (!controller.signal.aborted) console.error('check-sso failed:', e);
      }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [email]);

  return result?.email === email && result.ssoRequired;
}

interface InviteInfo {
  valid: boolean;
  org_name: string;
  role: string;
  email: string;
}

function InviteContext({ token }: { token: string }) {
  const { data, loading, error } = useFetchData<InviteInfo | null>(
    `/dashboard/api/invite-info?token=${encodeURIComponent(token)}`,
    null,
  );

  if (loading) return null;
  if (error || !data?.valid) {
    return (
      <div style={{ textAlign: 'center', color: 'var(--accent-red)', fontSize: 14, marginBottom: 8 }}>
        This invite link is invalid or has expired.
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--accent-cyan-glow)',
      border: '1px solid oklch(0.88 0.19 110 / 0.2)',
      borderRadius: 6,
      padding: '10px 14px',
      fontSize: 13,
      color: 'var(--accent-cyan)',
      textAlign: 'center',
      marginBottom: 4,
    }}>
      You've been invited to join <strong>{data.org_name}</strong> as {data.role}.
    </div>
  );
}

export function SignInPageComponent() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');
  const oauthError = searchParams.get('error');
  const next = searchParams.get('next');
  const isCliAuth = next?.startsWith('/oauth/authorize') ?? false;

  const [signInState, setSignInState] = useState<SignInState>({ step: 'idle' });
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpLength, setOtpLength] = useState(0);
  const ssoRequired = useSSORequired(email);

  const googleStartParams = new URLSearchParams();
  if (inviteToken) googleStartParams.set('invite', inviteToken);
  if (next) googleStartParams.set('next', next);
  const googleStartUrl = googleStartParams.size
    ? `/dashboard/auth/google/start?${googleStartParams}`
    : '/dashboard/auth/google/start';

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSignInState({ step: 'sending' });
    try {
      if (await checkSSO(email)) {
        const params = new URLSearchParams({ email });
        if (inviteToken) params.set('invite', inviteToken);
        if (next) params.set('next', next);
        window.location.href = `/dashboard/auth/saml/start?${params}`;
        return;
      }
      const res = await fetch('/dashboard/api/send-auth-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [CSRF_HEADER_NAME]: getCSRFToken(),
        },
        body: JSON.stringify({ email, ...(inviteToken ? { invite: inviteToken } : {}), ...(next ? { next } : {}) }),
      });
      const data = await res.json();
      if (!res.ok) {
        // 422 validation errors have a non-string detail.
        const message = typeof data.detail === 'string' ? data.detail : SEND_ERROR_MESSAGE;
        setSignInState({ step: 'send_error', message });
        return;
      }
      setOtpLength(data.otp_length);
      setOtp('');
      setSignInState({ step: 'otp_input' });
    } catch (e) {
      console.error('send-auth-email failed:', e);
      setSignInState({ step: 'send_error', message: SEND_ERROR_MESSAGE });
    }
  }

  async function submitOtp(otpValue: string) {
    setSignInState({ step: 'verifying' });
    try {
      const res = await fetch('/dashboard/api/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [CSRF_HEADER_NAME]: getCSRFToken(),
        },
        body: JSON.stringify({ email, otp: otpValue }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSignInState({ step: 'otp_input', otpError: data.detail ?? 'Verification failed. Please try again.' });
        return;
      }
      window.location.href = data.redirect_url;
    } catch (e) {
      console.error('verify-otp failed:', e);
      setSignInState({ step: 'otp_input', otpError: 'Something went wrong. Please try again.' });
    }
  }

  function handleOtpSubmit(e: React.FormEvent) {
    e.preventDefault();
    submitOtp(otp);
  }

  const showingOtp = signInState.step === 'otp_input' || signInState.step === 'verifying';

  return (
    <SignInPage>
      <WaveformBackground />

      <SignInWordmark>
        <img src="/static/guava-logo.png" alt="Guava" style={{ height: 400, filter: 'grayscale(1) brightness(0.75)', margin: '-80px 0' }} />
      </SignInWordmark>
      <SignInTagline>Voice AI for developers</SignInTagline>

      <SignInCard>
        {isCliAuth && <div style={{ fontSize: 16, color: 'var(--text-primary)', textAlign: 'center', paddingBottom: '16px' }}>
          <strong>Guava CLI is waiting to authenticate</strong>
          <div style={{ marginTop: 4, fontSize: 13, opacity: 0.85 }}>
            Sign in below, then follow the instructions to complete the login process.
          </div>
        </div>}
        {inviteToken && <InviteContext token={inviteToken} />}

        {oauthError && (
          <InlineError style={{ textAlign: 'center' }}>
            {oauthError === 'sso_required'
              ? 'Your organization requires signing in with SSO.'
              : oauthError === 'sso_failed'
                ? 'SSO sign-in failed. Please try again or contact your administrator.'
                : 'Google sign-in was cancelled or failed. Please try again.'}
          </InlineError>
        )}

        {showingOtp ? (
          <form onSubmit={handleOtpSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ textAlign: 'center', color: 'var(--accent-cyan)', fontSize: 13, padding: '4px 0' }}>
              Enter the {otpLength}-digit code sent to <strong>{email}</strong>.
            </div>
            <Input
              type="text"
              inputMode="numeric"
              pattern={`\\d{${otpLength}}`}
              maxLength={otpLength}
              placeholder={'0'.repeat(otpLength)}
              value={otp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setOtp(value);
                if (value.length === otpLength) {
                  submitOtp(value);
                }
              }}
              autoFocus
              required
              style={{ textAlign: 'center', letterSpacing: '0.3em', fontSize: 20 }}
            />
            {signInState.step === 'otp_input' && signInState.otpError && (
              <InlineError>{signInState.otpError}</InlineError>
            )}
            <PrimaryButton
              type="submit"
              disabled={signInState.step === 'verifying' || otp.length !== otpLength}
              style={{ fontFamily: "'Jost', sans-serif", textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 13 }}
            >
              {signInState.step === 'verifying' ? 'Verifying…' : 'Sign in'}
            </PrimaryButton>
            <SecondaryButton
              type="button"
              onClick={() => { setSignInState({ step: 'email_input' }); setOtp(''); }}
              style={{ fontFamily: "'Jost', sans-serif", textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 13 }}
            >
              Use a different email
            </SecondaryButton>
          </form>
        ) : (
          <>
            <a href={googleStartUrl} style={{ textDecoration: 'none' }}>
              <PrimaryButton style={{ width: '100%', justifyContent: 'center', gap: 10, fontFamily: "'Jost', sans-serif", textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 13 }}>
                <GoogleIcon />
                Continue with Google
              </PrimaryButton>
            </a>

            <Divider>or</Divider>

            {signInState.step === 'idle' ? (
              <SecondaryButton
                style={{ width: '100%', justifyContent: 'center', fontFamily: "'Jost', sans-serif", textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 13 }}
                onClick={() => setSignInState({ step: 'email_input' })}
              >
                Continue with Email
              </SecondaryButton>
            ) : (
              <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  required
                />
                {signInState.step === 'send_error' && (
                  <InlineError>{signInState.message}</InlineError>
                )}
                <PrimaryButton
                  type="submit"
                  disabled={signInState.step === 'sending' || !email}
                  style={{ fontFamily: "'Jost', sans-serif", textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 13 }}
                >
                  {signInState.step === 'sending' ? 'Sending…' : ssoRequired ? 'Continue with SSO' : 'Send sign-in code'}
                </PrimaryButton>
              </form>
            )}
          </>
        )}
      </SignInCard>
    </SignInPage>
  );
}
